// Режим «Игра с движком» поверх любой доски (разбор / тренажёр зевков / дебюты).
// Юзер ходит на MiniBoard, движок отвечает bestmove через планировщик с задержкой
// ~400мс. Машина состояний — чистый PlayLoop (services/play-engine.ts, покрыт
// vitest): ход с доски принимается только в очередь юзера, движок отвечает ровно
// один раз. ⏮◀▶⏭ работают и в игре — это откат/redo по ленте партии с ботом
// (takeBack/toStart/forward/toLatest). 💡 (setShowBest) — аддитивный тумблер:
// on-demand стрелка bestmove текущей позиции ОДНОВРЕМЕННО с игрой (PlayHint.sync).
import { winPct } from '@ext/core/evals';
import type { MiniBoard } from '@ext/panel/board/mini-board';
import { t } from '../i18n';
import { haptic, notify } from '../tg';
import {
  ENGINE_DELAY_MS,
  PlayHint,
  PlayLoop,
  turnOf,
  type AnalyzeFn,
  type EngineMove,
  type PlayResult,
  type Recorded,
} from '../services/play-engine';

export interface PlayHooks {
  board: MiniBoard;
  /** Куда смонтировать панель режима. */
  host: HTMLElement;
  /** On-demand анализ позиции (обычно sched.requestPriority). */
  analyze: AnalyzeFn;
  /** Подпись кнопки выхода (напр. «Вернуться к партии»). */
  exitLabel: string;
  /** Вернуть хост-экран в исходное состояние. */
  onExit: () => void;
  /** Опц.: каждая позиция после хода (юзера/движка) — хост может писать её в свою навигацию. */
  onPosition?: (fen: string, last: { from: string; to: string } | null) => void;
  /** Опц.: 💡 считает on-demand стрелку (для спиннера на кнопке). */
  onHintBusy?: (busy: boolean) => void;
  /** Снимок ленты после каждого изменения, включая итог. */
  onRecord?: (record: Recorded[], result: PlayResult) => void;
  /** Оценка белых для общей вертикальной шкалы хоста. */
  onEval?: (cpWhite: number | null) => void;
}

export class PlayEngineController {
  active = false;
  private panel: HTMLElement;
  private badgeEl!: HTMLElement;
  private evalEl!: HTMLElement;
  private movesEl!: HTMLElement;
  private resultEl!: HTMLElement;
  private loop: PlayLoop | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private hint: PlayHint;
  private showBest = false;
  private userSide: 'w' | 'b' = 'w';
  private changeCb: (() => void) | null = null;

  constructor(private hooks: PlayHooks) {
    this.panel = document.createElement('section');
    this.panel.className = 'card play-panel hidden';
    this.panel.innerHTML = `
      <div class="row between gap12">
        <span class="badge good" data-play-badge></span>
        <button type="button" class="nav-button" data-play-exit></button>
      </div>
      <div class="play-eval"><i data-play-eval></i></div>
      <p class="mono tiny play-moves" data-play-moves></p>
      <div class="feedback-card play-result hidden" data-play-result></div>`;
    hooks.host.appendChild(this.panel);
    this.badgeEl = this.panel.querySelector('[data-play-badge]')!;
    this.evalEl = this.panel.querySelector('[data-play-eval]')!;
    this.movesEl = this.panel.querySelector('[data-play-moves]')!;
    this.resultEl = this.panel.querySelector('[data-play-result]')!;
    const exit = this.panel.querySelector<HTMLButtonElement>('[data-play-exit]')!;
    exit.textContent = hooks.exitLabel;
    exit.addEventListener('click', () => this.stop());
    this.hint = new PlayHint(hooks.analyze, {
      onArrow: (a) => this.hooks.board.setArrow(a),
      onBusy: (busy) => this.hooks.onHintBusy?.(busy),
    });
  }

  /** Запустить игру из позиции fen; юзер играет стороной userSide. */
  start(fen: string, userSide: 'w' | 'b'): void {
    this.loop?.stop();
    this.active = true;
    this.userSide = userSide;
    this.loop = new PlayLoop(userSide, fen, this.hooks.analyze, {
      onEngineMove: (m, beforeFen) => this.showEngineMove(m, beforeFen),
      onEval: (cp) => this.setEval(cp),
      onFinish: (result, by) => this.finish(result, by),
    });
    this.badgeEl.textContent = t('play.badge');
    this.resultEl.classList.add('hidden');
    this.panel.classList.remove('hidden');
    this.hint.hide();
    this.hooks.board.setArrow(null);
    this.hooks.board.setMoveMarker(null);
    this.hooks.board.setOrientation(userSide === 'w' ? 'white' : 'black');
    this.hooks.board.setPosition(fen, null);
    this.setEval(null);
    this.renderMoves();
    this.syncHint();
    this.emitChange();
    if (turnOf(fen) !== userSide) this.scheduleEngine(); // движок открывает
  }

  /** Текущая позиция модели игры (для on-demand подсказки). */
  get fen(): string {
    return this.loop?.fen ?? '';
  }

  /** Сейчас ход юзера (движок не думает) — можно просить подсказку. */
  get waitingUser(): boolean {
    return this.loop?.waitingUser ?? false;
  }

  /** Состояние навигации по ленте партии (для тулбара ⏮◀▶⏭). */
  navState(): { atStart: boolean; atEnd: boolean } {
    return { atStart: !this.loop?.canBack, atEnd: !this.loop?.canForward };
  }

  /** Подписка тулбара на изменения (ход бота/финиш/навигация) — обновить ⏮◀▶⏭. */
  hookChange(fn: () => void): void {
    this.changeCb = fn;
  }

  private emitChange(): void {
    this.changeCb?.();
    this.hooks.onRecord?.(this.loop?.record ?? [], this.loop?.finished ?? null);
  }

  get record(): Recorded[] { return this.loop?.record ?? []; }

  get result(): PlayResult { return this.loop?.finished ?? null; }

  /** 💡 аддитивный тумблер — стрелка bestmove ОДНОВРЕМЕННО с игрой. */
  setShowBest(on: boolean): void {
    this.showBest = on;
    this.syncHint();
  }

  private syncHint(): void {
    if (this.active && this.loop) void this.hint.sync(this.loop.fen, this.showBest);
    else this.hint.hide();
  }

  /** Ход с доски (роутится хостом, когда active). Не в очередь юзера — откатываем доску. */
  handleUserMove(san: string, fen: string): void {
    if (!this.active || !this.loop) return;
    if (!this.loop.acceptUserMove(fen, san)) {
      this.hooks.board.setPosition(this.loop.fen, null); // доска разъехалась с моделью
      return;
    }
    this.renderMoves();
    this.hooks.onPosition?.(fen, null);
    this.syncHint();
    this.emitChange();
    haptic('medium');
    if (!this.loop.finished) this.scheduleEngine();
  }

  // --- навигация по ленте партии (⏮◀▶⏭ при active) ----------------------------
  takeBack(): void {
    this.loop?.takeBack();
    this.afterNav();
  }

  toStart(): void {
    this.loop?.toStart();
    this.afterNav();
  }

  forward(): void {
    this.loop?.forward();
    this.afterNav();
  }

  toLatest(): void {
    this.loop?.toLatest();
    this.afterNav();
  }

  /** Перерисовать доску/ленту/оценку/итог после отката-redo и, если нужно, доиграть. */
  private afterNav(): void {
    const loop = this.loop;
    if (!loop) return;
    const cur = loop.current;
    this.hooks.board.setPosition(cur.fen, cur.last);
    this.renderMoves();
    this.setEval(cur.cpWhite);
    this.showResult(loop.finished);
    this.syncHint();
    this.emitChange();
    if (loop.resume()) this.scheduleEngine(); // очередь бота — доигрывает с этого кадра
  }

  private scheduleEngine(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.loop?.engineMove();
    }, ENGINE_DELAY_MS);
  }

  private showEngineMove(m: EngineMove, _beforeFen: string): void {
    const last = { from: m.uci.slice(0, 2), to: m.uci.slice(2, 4) };
    this.hooks.board.setPosition(m.fen, last);
    this.renderMoves();
    this.hooks.onPosition?.(m.fen, last);
    this.syncHint();
    this.emitChange();
  }

  private renderMoves(): void {
    const rec: Recorded[] = this.loop?.record ?? [];
    const out: string[] = [];
    rec.forEach((m, i) => {
      const num = m.white ? `${m.moveNo}.` : i === 0 ? `${m.moveNo}…` : '';
      out.push(`${num}${m.san}`.trim());
    });
    this.movesEl.textContent = out.join(' ');
  }

  private setEval(cpWhite: number | null): void {
    const pct = cpWhite == null ? 50 : winPct(cpWhite);
    this.evalEl.style.width = `${Math.round(pct)}%`;
    this.hooks.onEval?.(cpWhite);
  }

  private finish(result: Exclude<PlayResult, null>, by: 'user' | 'engine'): void {
    notify(result === 'checkmate' && by === 'user' ? 'success' : result === 'checkmate' ? 'error' : 'warning');
    this.showResult(result);
    this.emitChange();
  }

  /**
   * Показать/снять плашку итога (снимается при откате от мата). Победителя матом
   * восстанавливаем по стороне на ходу — работает и для live-финиша, и для ⏭ к мату.
   */
  private showResult(result: PlayResult): void {
    if (!result) {
      this.resultEl.classList.add('hidden');
      return;
    }
    let key: string;
    if (result === 'checkmate') {
      const userMated = this.loop != null && turnOf(this.loop.fen) === this.userSide;
      key = userMated ? 'play.you_lose' : 'play.you_win';
    } else {
      key = result === 'stalemate' ? 'play.stalemate' : 'play.draw';
    }
    this.resultEl.textContent = t(key);
    this.resultEl.classList.remove('hidden');
  }

  /** Выйти из режима игры и вернуть хост-экран. */
  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.loop?.stop();
    this.loop = null;
    this.active = false;
    this.hint.hide();
    this.panel.classList.add('hidden');
    this.hooks.onExit();
  }
}
